import { WeightUnit } from "@redotech/cockroachdb-codegen/tables/product/weight-unit";
import { shopifyProvinces } from "@redotech/locale/provinces";
import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { LoadState, useLoad } from "@redotech/react-util/load";
import { useSearch } from "@redotech/react-util/search";
import {
  AESExemptionType,
  ContentsType,
  getContentsTypeLabel,
  getNonDeliveryOptionLabel,
  getTaxIdTypeLabel,
  NonDeliveryOption,
  OutboundShipmentTaxId,
  RedactedTaxIdentifier,
  TaxIdEntity,
  TaxIdType,
  TaxIdUsage,
  USPSExemptionCode,
} from "@redotech/redo-fulfillment-customs-common/customs";
import {
  CustomsDeclaration,
  customsDeclarationsAreEqual,
  HSCodeSource,
  ManufacturerAddress,
} from "@redotech/redo-fulfillment-customs-common/customs-declaration";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { HarmonizationCodes } from "@redotech/redo-fulfillment-harmonization-codes/harmonization-codes";
import { WeightFormat } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import { convertWeight } from "@redotech/redo-fulfillment-outbound-labels-common/util";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import StarsIcon from "@redotech/redo-icons/stars-01-gradient.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import {
  getCountryNameFromCode,
  RedoShopifyCountryCode,
} from "@redotech/redo-inventory-common/fulfillment-country-code";
import { InteractWithScaleProvider } from "@redotech/redo-merchant-app-common/components/weight-widget/interact-with-scale-context";
import { TotalWeightWidget } from "@redotech/redo-merchant-app-common/components/weight-widget/total-weight-widget";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCollapseSubsection } from "@redotech/redo-web/arbiter-components/collapse/redo-collapse-section";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  BadgeRedoListItem,
  RedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoTagsDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { Form } from "@redotech/redo-web/form";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import {
  nonEmptySingleErrorValidator,
  numberValidator,
} from "@redotech/ui/validators";
import Fuse from "fuse.js";
import { memo, useEffect, useId, useMemo, useRef, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";

const HS_CODE_WARNING_LENGTH = 6;

export const HarmonizationInput = memo(function HarmonizationInput({
  disabled,
  label,
  placeholder,
  value,
  setValue,
  hsCodeGenerated,
}: {
  disabled: boolean;
  label: string;
  placeholder: string;
  value: string;
  setValue: (value: string) => void;
  hsCodeGenerated: boolean;
}) {
  const [inputValue, setInputValue] = useState(value);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const debouncedSearchTerm = useDebounce(inputValue, 200);

  const harmonizationItems: RedoListItem<string>[] = useMemo(() => {
    return HarmonizationCodes.map((code) => ({
      id: code.hscode,
      type: "text" as const,
      text: `${code.hscode} - ${code.description}`,
      value: code.hscode,
    }));
  }, []);

  const searcher = useMemo(
    () =>
      new Fuse(harmonizationItems, {
        keys: ["value", "text"],
        threshold: 0.4,
        includeScore: true,
      }),
    [harmonizationItems],
  );

  const searchResults = useSearch(
    searcher,
    harmonizationItems,
    debouncedSearchTerm,
  );

  const filteredResults = useMemo(() => {
    if (!debouncedSearchTerm) {
      return [];
    }
    return searchResults.slice(0, 8);
  }, [searchResults, debouncedSearchTerm]);

  const handleInputChange = useHandler((newValue: string) => {
    setInputValue(newValue);
    setValue(newValue);
    setIsDropdownOpen(true);
  });

  const handleSelectItem = useHandler(
    ({ item }: { item: RedoListItem<string> }) => {
      setInputValue(item.value);
      setValue(item.value);
      setIsDropdownOpen(false);
    },
  );

  const showHsCodeWarningBorder = inputValue.length < HS_CODE_WARNING_LENGTH;

  return (
    <Flex w="full">
      <Flex ref={inputRef} w="full">
        <RedoTextInput
          errorText={
            showHsCodeWarningBorder
              ? `HS codes may need to be ${HS_CODE_WARNING_LENGTH} characters for certain carriers`
              : undefined
          }
          fullWidth
          label={label}
          placeholder={placeholder}
          setValue={handleInputChange}
          state={
            disabled
              ? "disabled"
              : showHsCodeWarningBorder
                ? "error"
                : "default"
          }
          value={inputValue}
        />
        {hsCodeGenerated && (
          <Tooltip title="This is an AI generated HS code">
            <Flex flexDirection="column" justify="center" ml="xs" mt="xl">
              <StarsIcon />
            </Flex>
          </Tooltip>
        )}
      </Flex>
      {filteredResults.length > 0 && isDropdownOpen && (
        <RedoListDropdown
          dropdownAnchor={inputRef.current}
          dropdownOpen={isDropdownOpen}
          fitToAnchor
          items={filteredResults}
          itemSelected={handleSelectItem}
          setDropdownOpen={setIsDropdownOpen}
        />
      )}
    </Flex>
  );
});

const customsDeclarationForm = groupInput({
  description: input<string>({ validator: nonEmptySingleErrorValidator }),
  quantity: input<number>({
    validator: numberValidator({ min: 1, maxDecimalPlaces: 0 }),
  }),
  itemValue: input<number>({
    validator: numberValidator({ min: 0.0, maxDecimalPlaces: 2 }),
  }),
  itemWeight: groupInput({
    value: input<number>({ validator: numberValidator({ exclusiveMin: 0 }) }),
    unit: input<WeightUnit>(),
  }),
  harmonizationCode: input<string | null | undefined>(),
  countryOfOrigin: input<RedoShopifyCountryCode>({
    validator: nonEmptySingleErrorValidator,
  }),
  manufacturer: input<string | null | undefined>(),
  manufacturerMid: input<string | null | undefined>(),
  manufacturerAddress: groupInput({
    street1: input<string | null | undefined>(),
    street2: input<string | null | undefined>(),
    city: input<string | null | undefined>(),
    state: input<string | null | undefined>(),
    postalCode: input<string | null | undefined>(),
    country: input<RedoShopifyCountryCode | null | undefined>(),
  }),
});
type CustomsDeclarationsForm = InputSchema.Value<typeof customsDeclarationForm>;
const defaultManufacturerAddress = {
  street1: null,
  street2: null,
  city: null,
  state: null,
  postalCode: null,
  country: null,
};
const defaultCustomsDeclaration = {
  description: "",
  quantity: 1,
  itemValue: 1,
  itemWeight: { value: 1, unit: WeightUnit.Oz },
  harmonizationCode: null,
  countryOfOrigin: RedoShopifyCountryCode.Us,
  manufacturer: null,
  manufacturerMid: null,
  manufacturerAddress: defaultManufacturerAddress,
};

function emptyToUndefined(
  value: string | null | undefined,
): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function normalizeManufacturerAddress(
  address: CustomsDeclarationsForm["manufacturerAddress"],
): ManufacturerAddress | undefined {
  const normalized: ManufacturerAddress = {
    street1: emptyToUndefined(address.street1),
    street2: emptyToUndefined(address.street2),
    city: emptyToUndefined(address.city),
    state: emptyToUndefined(address.state),
    postalCode: emptyToUndefined(address.postalCode),
    country: address.country ?? undefined,
  };
  return Object.values(normalized).some((v) => v != null)
    ? normalized
    : undefined;
}

const DeclarationCard = memo(function DeclarationCard({
  isEditable,
  index,
  declaration,
  onSave,
  onDelete,
}: {
  isEditable: boolean;
  index: number;
  declaration: CustomsDeclaration;
  onSave?: (declaration: CustomsDeclaration) => void;
  onDelete?: () => void;
}) {
  const team = useRequiredContext(TeamContext);
  const weightFormat = team?.settings?.outboundLabels?.weightFormat;

  const value: CustomsDeclarationsForm = useMemo(
    () => ({
      description: declaration.description,
      quantity: declaration.quantity,
      itemValue: declaration.itemValue,
      itemWeight: declaration.itemWeight,
      harmonizationCode: declaration.harmonizationCode,
      countryOfOrigin: declaration.countryOfOrigin,
      manufacturer: declaration.manufacturer,
      manufacturerMid: declaration.manufacturerMid,
      manufacturerAddress: {
        ...defaultManufacturerAddress,
        ...declaration.manufacturerAddress,
      },
    }),
    [declaration],
  );

  const input = useInput(
    customsDeclarationForm,
    value || defaultCustomsDeclaration,
  );

  const countryOptions: RedoListItem<RedoShopifyCountryCode>[] = useMemo(
    () =>
      Object.values(RedoShopifyCountryCode).map((code) => ({
        id: code,
        type: "text",
        text: getCountryNameFromCode(code),
        value: code,
      })),
    [],
  );

  const [countryOfOriginSearch, setCountryOfOriginSearch] = useState("");
  const [manufacturerCountrySearch, setManufacturerCountrySearch] =
    useState("");
  const [manufacturerProvinceSearch, setManufacturerProvinceSearch] =
    useState("");

  const filterCountryOptions = useHandler((search: string) => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return countryOptions;
    }
    return countryOptions.filter(
      (o) =>
        (o.type === "text" && o.text.toLowerCase().includes(query)) ||
        o.value.toLowerCase().includes(query),
    );
  });

  const filteredCountryOptions = useMemo(
    () => filterCountryOptions(countryOfOriginSearch),
    [filterCountryOptions, countryOfOriginSearch],
  );

  const filteredManufacturerCountryOptions = useMemo(
    () => filterCountryOptions(manufacturerCountrySearch),
    [filterCountryOptions, manufacturerCountrySearch],
  );

  // DDP carriers (e.g. Chit Chats) require the ISO subdivision code, not the full
  // name; offer known codes per country and fall back to free text otherwise.
  const manufacturerCountry = input.inputs.manufacturerAddress.inputs.country;
  const manufacturerState = input.inputs.manufacturerAddress.inputs.state;
  const availableManufacturerProvinces = useMemo(
    () =>
      shopifyProvinces.filter(
        (p) => p.countryCode === manufacturerCountry.value,
      ),
    [manufacturerCountry.value],
  );
  const manufacturerProvinceOptions: RedoListItem<string | null>[] =
    useMemo(() => {
      const query = manufacturerProvinceSearch.trim().toLowerCase();
      const matches = query
        ? availableManufacturerProvinces.filter(
            (p) =>
              p.province.toLowerCase().includes(query) ||
              p.provinceCode.toLowerCase().includes(query),
          )
        : availableManufacturerProvinces;
      return [
        { id: "none", type: "text" as const, text: "None", value: null },
        ...matches.map(({ province, provinceCode }) => ({
          id: provinceCode,
          type: "text" as const,
          text: `${province} (${provinceCode})`,
          value: provinceCode,
        })),
      ];
    }, [availableManufacturerProvinces, manufacturerProvinceSearch]);
  const selectedManufacturerProvinceItem = useMemo(() => {
    const current = manufacturerState.value;
    if (current) {
      const match = availableManufacturerProvinces.find(
        (p) => p.provinceCode === current,
      );
      return {
        id: current,
        type: "text" as const,
        text: match ? `${match.province} (${match.provinceCode})` : current,
        value: current,
      };
    }
    return { id: "none", type: "text" as const, text: "None", value: null };
  }, [manufacturerState.value, availableManufacturerProvinces]);

  const handleSave = useHandler(() => {
    if (onSave) {
      onSave({
        ...declaration,
        description: description.value,
        quantity: quantity.value,
        itemValue: itemValue.value,
        itemWeight: itemWeight.value,
        harmonizationCode: harmonizationCode.value,
        hsCodeSource: HSCodeSource.MANUAL,
        countryOfOrigin: countryOfOrigin.value,
        manufacturer: emptyToUndefined(manufacturer.value),
        manufacturerMid: emptyToUndefined(manufacturerMid.value),
        manufacturerAddress: normalizeManufacturerAddress(
          manufacturerAddress.value,
        ),
      });
    }
  });

  const id = useId();

  const {
    description,
    quantity,
    itemValue,
    itemWeight,
    harmonizationCode,
    countryOfOrigin,
    manufacturer,
    manufacturerMid,
    manufacturerAddress,
  } = input.inputs;

  // Collapse the DDP-only Manufacturer section by default, but auto-expand when
  // this declaration already has data so entered values stay visible.
  const manufacturerHasData =
    !!manufacturer.value ||
    !!manufacturerMid.value ||
    !!manufacturerAddress.inputs.street1.value ||
    !!manufacturerAddress.inputs.street2.value ||
    !!manufacturerAddress.inputs.city.value ||
    !!manufacturerAddress.inputs.state.value ||
    !!manufacturerAddress.inputs.postalCode.value ||
    !!manufacturerAddress.inputs.country.value;

  return (
    <Form id={id} initial={value} input={input} onSubmit={handleSave}>
      <Flex
        bgColor="secondary"
        borderColor="primary"
        borderStyle="solid"
        borderWidth="1px"
        flexDirection="column"
        gap="xl"
        p="xl"
        radius="md"
      >
        <Flex justify="space-between" w="full">
          <Text fontSize="xs" fontWeight="medium">
            Declaration {index + 1}
          </Text>
          {onDelete && (
            <RedoButton
              aria-label="Delete"
              disabled={!isEditable}
              hierarchy="tertiary"
              IconLeading={TrashIcon}
              onClick={onDelete}
              size="xs"
              theme="destructive"
            />
          )}
        </Flex>

        <Flex dir="column" gap="xl">
          <RedoTextInput
            label="Description"
            placeholder="Enter a description"
            setValue={(value) => description.setValue(value)}
            state={isEditable ? "default" : "disabled"}
            value={description.value}
          />
          <Flex dir="row" gap="md" w="full">
            <RedoIncrementDecrement
              disabled={!isEditable}
              fullWidth
              label="Quantity"
              min={1}
              placeholder="1"
              setValue={(value) => quantity.setValue(value)}
              size="small"
              value={quantity.value}
            />
          </Flex>
          <RedoIncrementDecrement
            description={`Total value: $${itemValue.value * quantity.value}`}
            disabled={!isEditable}
            fullWidth
            label="Item value"
            min={0}
            placeholder="0.00"
            prefix="$"
            setValue={(value) => {
              itemValue.setValue(value);
            }}
            size="small"
            step={0.01}
            value={itemValue.value}
          />
          <Flex dir="column" gap="xs">
            <InteractWithScaleProvider>
              <TotalWeightWidget
                disabled={!isEditable}
                onWeightChange={(weight) => {
                  itemWeight.inputs.value.setValue(weight.value);
                  itemWeight.inputs.unit.setValue(weight.unit);
                }}
                title="Item weight"
                weight={{
                  value: itemWeight.inputs.value.value,
                  unit: itemWeight.inputs.unit.value,
                }}
                weightFormat={weightFormat ?? WeightFormat.IMPERIAL}
              />
            </InteractWithScaleProvider>
            <Text color="secondary" fontSize="xs" textColor="tertiary">
              Total weight:{" "}
              {(
                convertWeight(
                  itemWeight.value,
                  weightFormat === WeightFormat.METRIC
                    ? WeightUnit.Kg
                    : WeightUnit.Lb,
                ).value * quantity.value
              ).toFixed(2)}{" "}
              {weightFormat === WeightFormat.METRIC ? "kg" : "lb"}
            </Text>
          </Flex>
          <HarmonizationInput
            disabled={!isEditable}
            hsCodeGenerated={
              declaration.hsCodeSource === HSCodeSource.CLASSIFICATION &&
              harmonizationCode.value != null &&
              !harmonizationCode.changed
            }
            label="Harmonization code"
            placeholder="Enter a harmonization code"
            setValue={(value) => harmonizationCode.setValue(value)}
            value={harmonizationCode.value ?? ""}
          />
          <RedoSingleSelectDropdownInput<RedoShopifyCountryCode>
            disabled={!isEditable}
            label="Country of origin"
            options={filteredCountryOptions}
            optionSelected={({ item }) => countryOfOrigin.setValue(item.value)}
            placeholder="Select a country"
            searchPlaceholder="Search countries"
            searchString={countryOfOriginSearch}
            selectedItem={countryOptions.find(
              (o) => o.value === countryOfOrigin.value,
            )}
            setSearchString={setCountryOfOriginSearch}
          />
          <RedoCollapseSubsection
            defaultCollapsed={!manufacturerHasData}
            title="Manufacturer"
          >
            <Flex dir="column" gap="md" pt="md">
              <RedoTextInput
                label="Manufacturer / company"
                placeholder="Enter the manufacturer or company"
                setValue={(value) => manufacturer.setValue(value)}
                state={isEditable ? "default" : "disabled"}
                value={manufacturer.value ?? ""}
              />
              <RedoTextInput
                description="Manufacturer Identification Number. Leave blank to auto-generate where supported."
                label="MID"
                placeholder="Enter the MID"
                setValue={(value) => manufacturerMid.setValue(value)}
                state={isEditable ? "default" : "disabled"}
                value={manufacturerMid.value ?? ""}
              />
              <RedoTextInput
                label="Street"
                placeholder="Street address"
                setValue={(value) =>
                  manufacturerAddress.inputs.street1.setValue(value)
                }
                state={isEditable ? "default" : "disabled"}
                value={manufacturerAddress.inputs.street1.value ?? ""}
              />
              <RedoTextInput
                label="Street 2"
                placeholder="Apt, suite, unit (optional)"
                setValue={(value) =>
                  manufacturerAddress.inputs.street2.setValue(value)
                }
                state={isEditable ? "default" : "disabled"}
                value={manufacturerAddress.inputs.street2.value ?? ""}
              />
              <Flex dir="row" gap="md" w="full">
                <Flex grow={1}>
                  <RedoTextInput
                    fullWidth
                    label="City"
                    placeholder="City"
                    setValue={(value) =>
                      manufacturerAddress.inputs.city.setValue(value)
                    }
                    state={isEditable ? "default" : "disabled"}
                    value={manufacturerAddress.inputs.city.value ?? ""}
                  />
                </Flex>
                <Flex grow={1}>
                  {availableManufacturerProvinces.length > 0 ? (
                    <RedoSingleSelectDropdownInput<string | null>
                      disabled={!isEditable}
                      label="Province / state"
                      options={manufacturerProvinceOptions}
                      optionSelected={({ item }) =>
                        manufacturerAddress.inputs.state.setValue(
                          item.value ?? undefined,
                        )
                      }
                      placeholder="Select a state / province"
                      searchPlaceholder="Search states / provinces"
                      searchString={manufacturerProvinceSearch}
                      selectedItem={selectedManufacturerProvinceItem}
                      setSearchString={setManufacturerProvinceSearch}
                    />
                  ) : (
                    <RedoTextInput
                      fullWidth
                      label="Province / state"
                      placeholder="Code, e.g. ON"
                      setValue={(value) =>
                        manufacturerAddress.inputs.state.setValue(value)
                      }
                      state={isEditable ? "default" : "disabled"}
                      value={manufacturerAddress.inputs.state.value ?? ""}
                    />
                  )}
                </Flex>
              </Flex>
              <RedoTextInput
                label="Postal code"
                placeholder="Postal code"
                setValue={(value) =>
                  manufacturerAddress.inputs.postalCode.setValue(value)
                }
                state={isEditable ? "default" : "disabled"}
                value={manufacturerAddress.inputs.postalCode.value ?? ""}
              />
              <RedoSingleSelectDropdownInput<RedoShopifyCountryCode>
                disabled={!isEditable}
                label="Country"
                options={filteredManufacturerCountryOptions}
                optionSelected={({ item }) => {
                  manufacturerAddress.inputs.country.setValue(item.value);
                  manufacturerAddress.inputs.state.setValue(undefined);
                }}
                placeholder="Select a country"
                searchPlaceholder="Search countries"
                searchString={manufacturerCountrySearch}
                selectedItem={countryOptions.find(
                  (o) => o.value === manufacturerAddress.inputs.country.value,
                )}
                setSearchString={setManufacturerCountrySearch}
              />
            </Flex>
          </RedoCollapseSubsection>
          {(() => {
            const fieldErrors = [
              { label: "Description", errors: description.errors },
              { label: "Quantity", errors: quantity.errors },
              { label: "Item value", errors: itemValue.errors },
              { label: "Item weight", errors: itemWeight.inputs.value.errors },
              { label: "Country of origin", errors: countryOfOrigin.errors },
            ].filter(({ errors }) => errors.length);
            return fieldErrors.length ? (
              <Flex dir="column" gap="xs">
                {fieldErrors.map(({ label, errors }) => (
                  <Text fontSize="xs" key={label} textColor="error">
                    {label}: {errors.join(", ")}
                  </Text>
                ))}
              </Flex>
            ) : null;
          })()}
          <Flex gap="md" justify="flex-end" w="full">
            {onSave && (
              <>
                <RedoButton
                  disabled={!input.changed || !isEditable}
                  hierarchy="secondary"
                  size="sm"
                  text="Cancel"
                  type="reset"
                />
                <RedoButton
                  disabled={
                    !!input.allErrors.length || !input.changed || !isEditable
                  }
                  hierarchy="primary"
                  size="sm"
                  text="Save"
                  type="submit"
                />
              </>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Form>
  );
});

interface CustomsUpdate {
  declarations?: CustomsDeclaration[];
  contentsType?: ContentsType | null;
  contentsExplanation?: string | null;
  additionalCustomsComments?: string | null;
  nonDeliveryOption?: NonDeliveryOption | null;
  uspsExemptionCode?: USPSExemptionCode | null;
  itn?: string | null;
  taxIdentifierIds?: string[] | null;
}

function buildCustomsUpdates(
  fulfillmentGroups: readonly FulfillmentGroupData[],
  update: CustomsUpdate,
): FulfillmentGroupUpdate[] {
  return fulfillmentGroups.map((group) => ({
    fulfillmentGroupId: group._id,
    ...(update.declarations !== undefined
      ? { customsDeclarations: update.declarations }
      : {}),
    ...(update.contentsType !== undefined
      ? { contentsType: update.contentsType }
      : {}),
    ...(update.contentsExplanation !== undefined
      ? { contentsExplanation: update.contentsExplanation }
      : {}),
    ...(update.additionalCustomsComments !== undefined
      ? { additionalCustomsComments: update.additionalCustomsComments }
      : {}),
    ...(update.nonDeliveryOption !== undefined
      ? { nonDeliveryOption: update.nonDeliveryOption }
      : {}),
    ...(update.uspsExemptionCode !== undefined
      ? { uspsExemptionCode: update.uspsExemptionCode }
      : {}),
    ...(update.itn !== undefined ? { itn: update.itn } : {}),
    ...(update.taxIdentifierIds !== undefined
      ? { taxIdentifierIds: update.taxIdentifierIds }
      : {}),
  }));
}

export const FulfillmentCustomsDeclarationsCard = memo(
  function FulfillmentCustomsDeclarationsCard({
    isEditable,
    fulfillmentGroups,
    updateGroups,
    customerTaxIds,
    onUpdateCustomerTaxIds,
  }: {
    isEditable: boolean;
    fulfillmentGroups: readonly FulfillmentGroupData[];
    updateGroups: (updates: FulfillmentGroupUpdate[]) => void;
    customerTaxIds?: OutboundShipmentTaxId[] | null;
    onUpdateCustomerTaxIds?: (taxIds: OutboundShipmentTaxId[]) => void;
  }) {
    const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);

    let customsDeclarations: CustomsDeclaration[] | null = null;
    let mixedCustomsDeclarations = false;

    const customsDeclarationLists = fulfillmentGroups.map((group) =>
      (group.customs?.declarations ?? []).sort((a, b) =>
        a.description.localeCompare(b.description),
      ),
    );

    if (customsDeclarationLists.every((list) => list.length === 0)) {
      customsDeclarations = [];
      mixedCustomsDeclarations = false;
    }

    if (
      customsDeclarationLists.every(
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        (list) => list.length === customsDeclarationLists[0].length,
      ) &&
      customsDeclarationLists.every((list) =>
        list.every((declaration, i) =>
          customsDeclarationsAreEqual({
            a: declaration,
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            b: customsDeclarationLists[0][i],
          }),
        ),
      )
    ) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      customsDeclarations = customsDeclarationLists[0];
      mixedCustomsDeclarations = false;
    } else {
      customsDeclarations = null;
      mixedCustomsDeclarations = true;
    }

    const taxIdentifiersLoad = useLoad(async () => {
      try {
        const { identifiers } = await rpcClient.getTaxIdentifiers(null);
        return identifiers;
      } catch (error) {
        console.error("Failed to load tax identifiers:", error);
      }
      return [];
    }, []);

    return (
      <FulfillmentCustomsDeclarations
        customerTaxIds={customerTaxIds}
        fulfillmentGroups={fulfillmentGroups}
        groupDeclarations={customsDeclarations ?? []}
        isEditable={isEditable}
        mixedCustomsDeclarations={mixedCustomsDeclarations}
        onUpdateCustomerTaxIds={onUpdateCustomerTaxIds}
        taxIdentifiersLoad={taxIdentifiersLoad}
        updateGroups={updateGroups}
      />
    );
  },
);

const CustomsSettingsForm = memo(function CustomsSettingsForm({
  isEditable,
  taxIdentifiersLoad,
  fulfillmentGroups,
  updateGroups,
  customerTaxIds,
  onUpdateCustomerTaxIds,
}: {
  isEditable: boolean;
  taxIdentifiersLoad: LoadState<RedactedTaxIdentifier[]>;
  fulfillmentGroups: readonly FulfillmentGroupData[];
  updateGroups: (updates: FulfillmentGroupUpdate[]) => void;
  customerTaxIds?: OutboundShipmentTaxId[] | null;
  onUpdateCustomerTaxIds?: (taxIds: OutboundShipmentTaxId[]) => void;
}) {
  const team = useRequiredContext(TeamContext);

  const taxIdTypeOptions: RedoListItem<TaxIdType>[] = useMemo(
    () =>
      Object.values(TaxIdType).map((type) => ({
        id: type,
        type: "text",
        text: getTaxIdTypeLabel(type),
        value: type,
      })),
    [],
  );

  const [customerTaxCountrySearch, setCustomerTaxCountrySearch] = useState("");

  const countryOptions: RedoListItem<RedoShopifyCountryCode>[] = useMemo(
    () =>
      Object.values(RedoShopifyCountryCode).map((code) => ({
        id: code,
        type: "text",
        text: getCountryNameFromCode(code),
        value: code,
      })),
    [],
  );

  const filteredCustomerTaxCountryOptions = useMemo(() => {
    const query = customerTaxCountrySearch.trim().toLowerCase();
    if (!query) {
      return countryOptions;
    }
    return countryOptions.filter(
      (o) =>
        (o.type === "text" && o.text.toLowerCase().includes(query)) ||
        o.value.toLowerCase().includes(query),
    );
  }, [countryOptions, customerTaxCountrySearch]);

  const defaultCustomsSettings = team?.settings?.outboundLabels?.customs;

  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);

  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

  const teamAesExemptionType =
    defaultCustomsSettings?.aesExemptionType || AESExemptionType.USPS;

  const teamTaxIdUsage =
    defaultCustomsSettings?.taxIdUsage || TaxIdUsage.OVER_2500;

  const hasHighCustomsValue =
    fulfillmentGroups?.some((group) => {
      return (
        (group.customs?.declarations?.reduce(
          (sum, declaration) =>
            sum + (declaration.itemValue ?? 0) * (declaration.quantity ?? 0),
          0,
        ) ?? 0) > 2500
      );
    }) ?? false;

  const shouldShowItn =
    teamAesExemptionType === AESExemptionType.ITN_TAX_ID &&
    (teamTaxIdUsage === TaxIdUsage.ALL ||
      (teamTaxIdUsage === TaxIdUsage.OVER_2500 && hasHighCustomsValue));

  const { contentsType, mixedContentsType } = fulfillmentGroups.every(
    (group) => group.contentsType === fulfillmentGroups?.[0]?.contentsType,
  )
    ? {
        contentsType:
          fulfillmentGroups?.[0]?.contentsType ||
          defaultCustomsSettings?.defaultContentsType ||
          ContentsType.MERCHANDISE,
        mixedContentsType: false,
      }
    : { contentsType: null, mixedContentsType: true };

  const { contentsExplanation, mixedContentsExplanation } =
    fulfillmentGroups.every(
      (group) =>
        group.contentsExplanation ===
        fulfillmentGroups?.[0]?.contentsExplanation,
    )
      ? {
          contentsExplanation:
            fulfillmentGroups?.[0]?.contentsExplanation || "",
          mixedContentsExplanation: false,
        }
      : { contentsExplanation: "", mixedContentsExplanation: true };

  const { additionalCustomsComments, mixedAdditionalCustomsComments } =
    fulfillmentGroups.every(
      (group) =>
        group.additionalCustomsComments ===
        fulfillmentGroups?.[0]?.additionalCustomsComments,
    )
      ? {
          additionalCustomsComments:
            fulfillmentGroups?.[0]?.additionalCustomsComments || "",
          mixedAdditionalCustomsComments: false,
        }
      : { additionalCustomsComments: "", mixedAdditionalCustomsComments: true };

  const { nonDeliveryOption, mixedNonDeliveryOption } = fulfillmentGroups.every(
    (group) =>
      group.nonDeliveryOption === fulfillmentGroups?.[0]?.nonDeliveryOption,
  )
    ? {
        nonDeliveryOption:
          fulfillmentGroups?.[0]?.nonDeliveryOption ||
          defaultCustomsSettings?.defaultNonDeliveryOption ||
          NonDeliveryOption.RETURN,
        mixedNonDeliveryOption: false,
      }
    : { nonDeliveryOption: null, mixedNonDeliveryOption: true };

  const { uspsExemptionCode, mixedUSPSExemptionCode } = fulfillmentGroups.every(
    (group) =>
      group.uspsExemptionCode === fulfillmentGroups?.[0]?.uspsExemptionCode,
  )
    ? {
        uspsExemptionCode:
          fulfillmentGroups?.[0]?.uspsExemptionCode ||
          defaultCustomsSettings?.uspsExemptionCode ||
          USPSExemptionCode.NOEEI_30_37_A,
        mixedUSPSExemptionCode: false,
      }
    : { uspsExemptionCode: null, mixedUSPSExemptionCode: true };

  const taxIdentifierIdList = fulfillmentGroups.map((group) =>
    (group.taxIdentifierIds ?? []).sort((a, b) => a.localeCompare(b)),
  );

  const { taxIdentifierIds, mixedTaxIdentifierIds } =
    taxIdentifierIdList.every(
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      (list) => list.length === taxIdentifierIdList[0].length,
    ) &&
    taxIdentifierIdList.every((list) =>
      list.every((id, i) => id === taxIdentifierIdList?.[0]?.[i]),
    )
      ? {
          taxIdentifierIds: taxIdentifierIdList[0],
          mixedTaxIdentifierIds: false,
        }
      : { taxIdentifierIds: null, mixedTaxIdentifierIds: true };

  const { itn, mixedItn } = fulfillmentGroups.every(
    (group) => group.itn === fulfillmentGroups?.[0]?.itn,
  )
    ? { itn: fulfillmentGroups[0]?.itn, mixedItn: false }
    : { itn: null, mixedItn: true };

  const taxIdentifierOptions: BadgeRedoListItem<string>[] = (
    taxIdentifiersLoad?.value || []
  ).map((taxId) => ({
    id: taxId._id,
    type: "badge",
    value: taxId._id,
    badge: {
      text: `${getTaxIdTypeLabel(taxId.taxIdType)} - ${taxId.nickname}`,
    },
  }));

  const contentsTypeOptions: RedoListItem<ContentsType>[] = Object.values(
    ContentsType,
  ).map((type) => ({
    id: type,
    type: "text",
    text: getContentsTypeLabel(type),
    value: type,
  }));

  const nonDeliveryOptions: RedoListItem<NonDeliveryOption>[] = Object.values(
    NonDeliveryOption,
  ).map((option) => ({
    id: option,
    type: "text",
    text: getNonDeliveryOptionLabel(option),
    value: option,
  }));

  const uspsExemptionCodeOptions: RedoListItem<USPSExemptionCode>[] =
    Object.values(USPSExemptionCode).map((code) => ({
      id: code,
      type: "text",
      text: code,
      value: code,
    }));

  const handleSaveSettings = useHandler(
    (update: {
      contentsType?: ContentsType;
      contentsExplanation?: string;
      nonDeliveryOption?: NonDeliveryOption;
      uspsExemptionCode?: USPSExemptionCode;
      taxIdentifierIds?: string[];
      itn?: string | null;
      additionalCustomsComments?: string;
    }) => {
      if (!isEditable) {
        return;
      }

      updateGroups(buildCustomsUpdates(fulfillmentGroups, update));
    },
  );

  return (
    <Flex dir="column" gap="xl">
      <Flex dir="column" gap="xl">
        <RedoSingleSelectDropdownInput<ContentsType>
          disabled={!isEditable}
          label="Contents type"
          options={contentsTypeOptions}
          optionSelected={({ item }) => {
            handleSaveSettings({ contentsType: item.value });
          }}
          placeholder={mixedContentsType ? "{mixed}" : "Select contents type"}
          selectedItem={contentsTypeOptions.find(
            (o) => o.value === contentsType,
          )}
        />
        <Flex dir="column" gap="xs">
          <RedoTextInput
            infoTooltip="Required for shipments to Mexico"
            label="Contents explanation"
            placeholder={
              mixedContentsExplanation
                ? "{mixed}"
                : "Enter contents explanation"
            }
            setValue={(value) => {
              handleSaveSettings({ contentsExplanation: value });
            }}
            state={isEditable ? "default" : "disabled"}
            value={contentsExplanation || ""}
          />
        </Flex>
        <Flex dir="column" gap="xs">
          <RedoTextInput
            infoTooltip="Any additional comments to send to customs"
            label="Additional comments (UPS only)"
            placeholder={
              mixedAdditionalCustomsComments
                ? "{mixed}"
                : "Enter additional comments"
            }
            setValue={(value) => {
              handleSaveSettings({ additionalCustomsComments: value });
            }}
            state={isEditable ? "default" : "disabled"}
            value={additionalCustomsComments || ""}
          />
        </Flex>
        <RedoSingleSelectDropdownInput<NonDeliveryOption>
          disabled={!isEditable}
          label="Non-delivery option"
          options={nonDeliveryOptions}
          optionSelected={({ item }) => {
            handleSaveSettings({ nonDeliveryOption: item.value });
          }}
          placeholder={
            mixedNonDeliveryOption ? "{mixed}" : "Select non-delivery option"
          }
          selectedItem={nonDeliveryOptions.find(
            (o) => o.value === nonDeliveryOption,
          )}
        />
        {/* Conditionally show either USPS Exemption Code or ITN based on team settings */}
        {teamAesExemptionType === AESExemptionType.USPS && (
          <RedoSingleSelectDropdownInput<USPSExemptionCode>
            disabled={!isEditable}
            label="USPS exemption code"
            options={uspsExemptionCodeOptions}
            optionSelected={({ item }) => {
              handleSaveSettings({ uspsExemptionCode: item.value });
            }}
            placeholder={
              mixedUSPSExemptionCode ? "{mixed}" : "Select USPS exemption code"
            }
            selectedItem={uspsExemptionCodeOptions.find(
              (o) => o.value === uspsExemptionCode,
            )}
          />
        )}

        {shouldShowItn && (
          <Flex dir="column" gap="xs">
            <RedoTextInput
              label="Internal transaction number (ITN)"
              placeholder={mixedItn ? "{mixed}" : "Enter ITN"}
              setValue={(value) => {
                handleSaveSettings({ itn: value });
              }}
              state={isEditable ? "default" : "disabled"}
              value={itn || ""}
            />
            <Text color="secondary" fontSize="xs" textColor="tertiary">
              Required for international shipments{" "}
              {teamTaxIdUsage === TaxIdUsage.OVER_2500 ? "over $2,500" : ""}
            </Text>
          </Flex>
        )}
        <Flex flexDirection="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Tax identifiers{" "}
            {taxIdentifierIds ? `(${taxIdentifierIds.length}/8)` : "(0/8)"}
          </Text>
          <RedoTagsDropdownInputButton
            disabled={
              !isEditable ||
              (taxIdentifierIds != null && taxIdentifierIds.length >= 8)
            }
            isDropdownOpen={dropdownOpen}
            onClick={() => {
              if (taxIdentifierIds && taxIdentifierIds.length >= 8) {
                return;
              }
              setDropdownOpen(!dropdownOpen);
            }}
            placeholder={
              mixedTaxIdentifierIds
                ? "{mixed}"
                : taxIdentifierIds && taxIdentifierIds.length >= 8
                  ? "Maximum 8 identifiers reached"
                  : "Select tax identifiers"
            }
            ref={setDropdownButtonRef}
            removeItem={(item) => {
              handleSaveSettings({
                taxIdentifierIds: [
                  ...(taxIdentifierIds?.filter((id) => id !== item.id) || []),
                ],
              });
            }}
            selectedItems={(taxIdentifierIds || []).reduce<
              BadgeRedoListItem<string>[]
            >((acc, id) => {
              const option = taxIdentifierOptions.find((o) => o.value === id);
              if (option) {
                acc.push(option);
              }
              return acc;
            }, [])}
            size="xs"
          />
          <RedoMultiselectDropdown<string>
            dropdownAnchor={dropdownButtonRef}
            dropdownOpen={
              dropdownOpen &&
              !(taxIdentifierIds && taxIdentifierIds.length >= 8)
            }
            fitToAnchor
            items={taxIdentifierOptions}
            itemsLoading={taxIdentifiersLoad.pending}
            selectedItems={(taxIdentifierIds || []).reduce<
              BadgeRedoListItem<string>[]
            >((acc, id) => {
              const option = taxIdentifierOptions.find((o) => o.value === id);
              if (option) {
                acc.push(option);
              }
              return acc;
            }, [])}
            selectionVariant="checkbox"
            setDropdownOpen={setDropdownOpen}
            setSelectedItems={(items) => {
              // Limit to 8 tax identifiers
              const limitedItems = items.slice(0, 8);
              handleSaveSettings({
                taxIdentifierIds: limitedItems.map(({ value }) => value),
              });
            }}
            size="xs"
          />
          {taxIdentifierIds && taxIdentifierIds.length >= 8 && (
            <Text color="error" fontSize="xs">
              Maximum 8 tax identifiers allowed
            </Text>
          )}
        </Flex>
        {onUpdateCustomerTaxIds && (
          <Flex flexDirection="column" gap="xs">
            <Text fontSize="xs" textColor="secondary">
              Customer tax identifiers
            </Text>
            <Flex flexDirection="column" gap="sm">
              {(customerTaxIds && customerTaxIds.length > 0
                ? customerTaxIds
                : [
                    {
                      entity: TaxIdEntity.Receiver,
                      tax_id: "",
                      tax_id_type: TaxIdType.TIN,
                      issuing_country: "",
                    },
                  ]
              ).map((taxId, index) => {
                const hasExisting =
                  !!customerTaxIds && customerTaxIds.length > 0;
                return (
                  <Flex dir="column" gap="sm" key={`customer-tax-${index}`}>
                    <Flex align="flex-end" gap="sm">
                      <RedoSingleSelectDropdownInput<TaxIdType>
                        disabled={!isEditable}
                        label="Type"
                        options={taxIdTypeOptions}
                        optionSelected={({ item }) => {
                          const updated = [...(customerTaxIds ?? [])];
                          updated[index] = {
                            ...taxId,
                            tax_id_type: item.value,
                          };
                          onUpdateCustomerTaxIds(updated);
                        }}
                        placeholder="Select type"
                        selectedItem={taxIdTypeOptions.find(
                          (o) => o.value === taxId.tax_id_type,
                        )}
                      />
                      <RedoSingleSelectDropdownInput<RedoShopifyCountryCode>
                        disabled={!isEditable}
                        label="Country"
                        options={filteredCustomerTaxCountryOptions}
                        optionSelected={({ item }) => {
                          const updated = [...(customerTaxIds ?? [])];
                          updated[index] = {
                            ...taxId,
                            issuing_country: item.value,
                          };
                          onUpdateCustomerTaxIds(updated);
                        }}
                        placeholder="Select country"
                        searchPlaceholder="Search countries"
                        searchString={customerTaxCountrySearch}
                        selectedItem={countryOptions.find(
                          (o) => o.value === taxId.issuing_country,
                        )}
                        setSearchString={setCustomerTaxCountrySearch}
                      />
                      {isEditable && hasExisting && (
                        <Flex align="center" mt="xl">
                          <RedoButton
                            aria-label="Delete"
                            hierarchy="tertiary"
                            IconLeading={TrashIcon}
                            onClick={() => {
                              onUpdateCustomerTaxIds(
                                (customerTaxIds ?? []).filter(
                                  (_, i) => i !== index,
                                ),
                              );
                            }}
                            size="xs"
                            theme="destructive"
                          />
                        </Flex>
                      )}
                    </Flex>
                    <RedoTextInput
                      label="Number"
                      setValue={(value) => {
                        const updated = [...(customerTaxIds ?? [])];
                        updated[index] = { ...taxId, tax_id: value };
                        onUpdateCustomerTaxIds(updated);
                      }}
                      size="sm"
                      state={isEditable ? "default" : "readonly"}
                      value={taxId.tax_id}
                    />
                  </Flex>
                );
              })}
            </Flex>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
});

const FulfillmentCustomsDeclarations = memo(
  function FulfillmentCustomsDeclarations({
    mixedCustomsDeclarations,
    groupDeclarations,
    isEditable,
    taxIdentifiersLoad,
    fulfillmentGroups,
    updateGroups,
    customerTaxIds,
    onUpdateCustomerTaxIds,
  }: {
    mixedCustomsDeclarations: boolean;
    groupDeclarations: CustomsDeclaration[];
    isEditable: boolean;
    taxIdentifiersLoad: LoadState<RedactedTaxIdentifier[]>;
    fulfillmentGroups: readonly FulfillmentGroupData[];
    updateGroups: (updates: FulfillmentGroupUpdate[]) => void;
    customerTaxIds?: OutboundShipmentTaxId[] | null;
    onUpdateCustomerTaxIds?: (taxIds: OutboundShipmentTaxId[]) => void;
  }) {
    const [declarations, setDeclarations] =
      useState<CustomsDeclaration[]>(groupDeclarations);
    const [newDeclaration, setNewDeclaration] =
      useState<CustomsDeclaration | null>(null);

    // Sync local state with parcel data when parcel being edited changes
    useEffect(() => {
      setDeclarations(groupDeclarations);
    }, [groupDeclarations]);

    // Declaration cards seed their editable form state once on mount (useInput
    // -> useState) and never re-seed when the declaration prop changes. Keying
    // by index alone reuses a card when the underlying declaration data updates
    // (shipment tab switch, void refetch, optimistic update), so its form state
    // goes stale and shows the wrong value / a spurious "unsaved" state. Keying
    // by the declaration's content remounts the card with fresh state whenever
    // the data for that slot changes, while staying stable during local edits.
    const declarationKey = (declaration: CustomsDeclaration, index: number) =>
      [
        index,
        declaration.productIdentifier ?? declaration.description,
        declaration.itemValue,
        declaration.quantity,
        declaration.itemWeight?.value,
        declaration.countryOfOrigin,
      ].join(":");

    const handleAddDeclaration = useHandler(() => {
      setNewDeclaration(defaultCustomsDeclaration);
    });

    const persistDeclarations = useHandler(
      (updatedDeclarations: CustomsDeclaration[]) => {
        updateGroups(
          buildCustomsUpdates(fulfillmentGroups, {
            declarations: updatedDeclarations,
          }),
        );
      },
    );

    const handleSaveNewDeclaration = useHandler(
      (declaration: CustomsDeclaration) => {
        const updatedDeclarations = [...declarations, declaration];
        setDeclarations(updatedDeclarations);
        setNewDeclaration(null);
        persistDeclarations(updatedDeclarations);
      },
    );

    const handleCancelNewDeclaration = useHandler(() => {
      setNewDeclaration(null);
    });

    const handleSaveDeclaration = useHandler(
      (index: number, updatedDeclaration: CustomsDeclaration) => {
        const updatedDeclarations = declarations.map((declaration, i) => {
          if (i === index) {
            return updatedDeclaration;
          }
          return declaration;
        });

        setDeclarations(updatedDeclarations);
        persistDeclarations(updatedDeclarations);
      },
    );

    const handleDeleteDeclaration = useHandler((index: number) => {
      const updatedDeclarations = declarations.filter((_, i) => i !== index);
      setDeclarations(updatedDeclarations);
      persistDeclarations(updatedDeclarations);
    });

    const handleDeleteAllDeclarations = useHandler(() => {
      setDeclarations([]);
      persistDeclarations([]);
    });

    return (
      <Flex dir="column" gap="lg" w="full">
        <Flex dir="column" gap="2xl">
          <Flex dir="column" gap="md" w="full">
            <CustomsSettingsForm
              customerTaxIds={customerTaxIds}
              fulfillmentGroups={fulfillmentGroups}
              isEditable={isEditable}
              onUpdateCustomerTaxIds={onUpdateCustomerTaxIds}
              taxIdentifiersLoad={taxIdentifiersLoad}
              updateGroups={updateGroups}
            />
          </Flex>
          <Flex
            borderBottomWidth="1px"
            borderColor="primary"
            borderStyle="solid"
          />
          <Flex dir="column" gap="md">
            <Flex align="center" justify="space-between">
              <Text fontSize="sm" fontWeight="semibold">
                Customs items
              </Text>
              {declarations.length > 0 && isEditable && (
                <RedoButton
                  disabled={!isEditable}
                  hierarchy="secondary"
                  IconLeading={TrashIcon}
                  onClick={handleDeleteAllDeclarations}
                  size="xs"
                  text="Delete all"
                />
              )}
            </Flex>
            {mixedCustomsDeclarations ? (
              <Flex
                bgColor="secondary"
                borderColor="primary"
                borderStyle="solid"
                borderWidth="1px"
                dir="column"
                p="xl"
                radius="md"
              >
                <Text color="secondary" fontSize="sm">
                  Mixed customs declarations. Edit to update all fulfillment
                  groups.
                </Text>
                {isEditable && (
                  <Flex justify="flex-end" mt="lg" w="full">
                    {!newDeclaration && (
                      <RedoButton
                        hierarchy="secondary"
                        IconLeading={PlusIcon}
                        onClick={handleAddDeclaration}
                        size="sm"
                        text="Add declaration"
                      />
                    )}
                  </Flex>
                )}
              </Flex>
            ) : (
              <>
                {declarations.map((declaration, index) => (
                  <DeclarationCard
                    declaration={declaration}
                    index={index}
                    isEditable={isEditable}
                    key={declarationKey(declaration, index)}
                    onDelete={
                      isEditable
                        ? () => handleDeleteDeclaration(index)
                        : undefined
                    }
                    onSave={
                      isEditable
                        ? (updatedDeclaration) =>
                            handleSaveDeclaration(index, updatedDeclaration)
                        : undefined
                    }
                  />
                ))}
                {!newDeclaration && (
                  <Flex justify="flex-end" w="full">
                    <RedoButton
                      disabled={!isEditable}
                      hierarchy="secondary"
                      IconLeading={PlusIcon}
                      onClick={handleAddDeclaration}
                      size="sm"
                      text="Add declaration"
                    />
                  </Flex>
                )}
              </>
            )}
            {isEditable && newDeclaration && (
              <DeclarationCard
                declaration={newDeclaration}
                index={declarations.length}
                isEditable={isEditable}
                key="new-declaration"
                onDelete={handleCancelNewDeclaration}
                onSave={handleSaveNewDeclaration}
              />
            )}
          </Flex>
        </Flex>
      </Flex>
    );
  },
);
